import { Link } from "react-router-dom";
import { ETSY_URL, INSTAGRAM_URL, TIKTOK_URL, LOGO_URL, trackEtsy } from "../data/site";
import { Instagram, ArrowUpRight } from "lucide-react";

function TikTokIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.59a8.16 8.16 0 0 0 4.93 1.61V7.74a4.85 4.85 0 0 1-2-1.05Z" />
    </svg>
  );
}

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-[#0B0B0B] border-t border-[#262626]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-4">
            <img
              src={LOGO_URL}
              alt="Kendi Collections"
              className="h-20 md:h-24 w-auto object-contain"
            />
            <p className="mt-6 max-w-sm text-[13px] leading-[1.85] text-white/55 font-light">
              Curated African heritage — handwoven baskets, beaded jewellery,
              wooden carvings, and contemporary art. Shipped worldwide from
              Melbourne via our Etsy boutique.
            </p>

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram-link"
                className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-500"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                data-testid="footer-tiktok-link"
                className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-500"
              >
                <TikTokIcon className="w-4 h-4" />
              </a>
              <a
                href={ETSY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEtsy("footer_etsy_badge")}
                data-testid="footer-etsy-icon-link"
                aria-label="Etsy shop"
                className="h-11 inline-flex items-center gap-2 px-4 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-500 text-[10px] tracking-[0.32em] uppercase"
              >
                Etsy Shop
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Shop (heritage primary) */}
          <div className="md:col-span-3">
            <h4 className="text-[11px] tracking-[0.42em] uppercase text-[#D4AF37] mb-5">
              Heritage
            </h4>
            <ul className="space-y-3 text-[13px] text-white/65">
              <li>
                <Link to="/" data-testid="footer-link-home" className="hover:text-[#D4AF37] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/heritage" data-testid="footer-link-heritage" className="hover:text-[#D4AF37] transition-colors">
                  Heritage Collection
                </Link>
              </li>
              <li>
                <a
                  href={ETSY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEtsy("footer_shop_heritage")}
                  data-testid="footer-shop-etsy"
                  className="hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5"
                >
                  Shop on Etsy <ArrowUpRight className="w-3 h-3" />
                </a>
              </li>
              <li>
                <Link to="/#stories" data-testid="footer-link-artisans" className="hover:text-[#D4AF37] transition-colors">
                  Artisan Stories
                </Link>
              </li>
            </ul>
          </div>

          {/* Other */}
          <div className="md:col-span-2">
            <h4 className="text-[11px] tracking-[0.42em] uppercase text-[#D4AF37] mb-5">
              More
            </h4>
            <ul className="space-y-3 text-[13px] text-white/65">
              <li>
                <Link to="/artisans" data-testid="footer-link-artisans-page" className="hover:text-[#D4AF37] transition-colors">
                  Artisans
                </Link>
              </li>
              <li>
                <Link to="/about" data-testid="footer-link-about" className="hover:text-[#D4AF37] transition-colors">
                  About
                </Link>
              </li>
              <li>
                <Link to="/support-artisans" data-testid="footer-link-support" className="hover:text-[#D4AF37] transition-colors">
                  Support Artisans
                </Link>
              </li>
              <li>
                <Link to="/dresses" data-testid="footer-link-dresses" className="hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2">
                  Dresses
                  <span className="text-[8px] tracking-[0.28em] uppercase text-[#D4AF37]/70 border border-[#D4AF37]/30 px-1.5 py-0.5">Soon</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Studio */}
          <div className="md:col-span-3">
            <h4 className="text-[11px] tracking-[0.42em] uppercase text-[#D4AF37] mb-5">
              Kendi Ecosystem
            </h4>
            <a
              href="https://moire.live"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                typeof window !== "undefined" &&
                window.trackMoire &&
                window.trackMoire("footer", "footer_ecosystem_link")
              }
              data-testid="footer-moire-link"
              className="block group"
            >
              <div className="font-serif-display italic text-2xl md:text-3xl text-gold-shimmer leading-tight">
                Moiré
              </div>
              <p className="mt-1 text-[11px] tracking-[0.28em] uppercase text-white/55">
                Personal Styling Intelligence
              </p>
              <p className="mt-2 text-[10px] tracking-[0.32em] uppercase text-white/40">
                Powered by Kendi Collections
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] group-hover:gap-2.5 transition-all">
                Discover Moiré
                <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </a>

            <div className="mt-6 pt-6 border-t border-[#262626]">
              <p className="text-[10px] tracking-[0.32em] uppercase text-white/40">KC-CINEM Design Archive</p>
              <Link
                to="/collections/kc-cinem/003-1-the-techcare-reaper"
                data-testid="footer-link-kc-cinem-003"
                className="mt-2 inline-block text-[13px] text-white/65 hover:text-[#D4AF37] transition-colors"
              >
                KC-CINEM 003.1 — The Techcare Reaper
              </Link>
            </div>

            <div className="mt-6 pt-6 border-t border-[#262626] text-[13px] text-white/65 leading-[1.85]">
              Melbourne, AU
              <br />
              Ships Worldwide
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#262626] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[10px] tracking-[0.32em] uppercase text-white/40">
            © {new Date().getFullYear()} Kendi Collections — All rights reserved.
          </p>
          <p className="text-[10px] tracking-[0.32em] uppercase text-white/40">
            African heritage <span className="text-[#D4AF37] mx-2">◆</span>
            Handmade <span className="text-[#D4AF37] mx-2">◆</span>
            Made in Melbourne
          </p>
        </div>
      </div>
    </footer>
  );
}
